import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Target, Loader2, Sparkles, Calendar, Activity, ShieldAlert,
  CheckCircle2, Library, ListTree, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const DISABILITY_OPTS = [
  { id: 'visual', ar: 'إعاقة بصرية', en: 'Visual' },
  { id: 'hearing', ar: 'إعاقة سمعية', en: 'Hearing' },
  { id: 'intellectual', ar: 'إعاقة ذهنية', en: 'Intellectual' },
  { id: 'cp', ar: 'شلل دماغي', en: 'Cerebral Palsy' },
  { id: 'limb', ar: 'بتر أطراف', en: 'Limb deficiency' },
  { id: 'wheelchair', ar: 'كرسي متحرك', en: 'Wheelchair' },
  { id: 'physical', ar: 'إعاقة جسدية', en: 'Physical' },
];

function ProgramCard({ p, isAr }) {
  return (
    <Card className="card-elevate h-full" data-testid={`tp-program-${p.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <Badge variant="outline" className="text-[10px] font-bold">{p.academy === 'paralympic' ? (isAr ? 'بارالمبية' : 'Paralympic') : (isAr ? 'أولمبياد خاص' : 'Special Olympics')}</Badge>
          <Badge className="text-[10px] font-bold bg-emerald-100 text-emerald-700 border-emerald-300">{p.level}</Badge>
        </div>
        <h3 className="text-base font-black mb-1 leading-tight">{isAr ? p.title_ar : (p.title_en || p.title_ar)}</h3>
        <div className="text-xs text-muted-foreground mb-2">
          {p.age_min}–{p.age_max} {isAr ? 'سنة' : 'yrs'} · {p.duration_weeks} {isAr ? 'أسبوع' : 'wk'} · {p.sessions_per_week}× {isAr ? 'أسبوعياً' : '/wk'}
        </div>
        <ul className="text-xs space-y-1 mb-2">
          {(p.objectives_ar || []).slice(0, 3).map((o, i) => (
            <li key={i} className="flex items-start gap-1"><CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" /><span>{o}</span></li>
          ))}
        </ul>
        {p.safety_notes_ar && (
          <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 mt-2 flex items-start gap-1">
            <ShieldAlert className="w-3 h-3 mt-0.5 text-amber-700 shrink-0" />
            <span className="text-amber-900">{p.safety_notes_ar}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function TrainingPrograms() {
  const [params] = useSearchParams();
  const { lang } = useApp();
  const isAr = lang === 'ar';
  const academyInit = params.get('academy') || 'all';
  const sportInit = params.get('sport') || 'all';

  // Catalogues
  const [sports, setSports] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [generated, setGenerated] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [fAcademy, setFAcademy] = useState(academyInit || 'all');
  const [fSport, setFSport] = useState(sportInit || 'all');
  const [fLevel, setFLevel] = useState('all');

  // Generator form
  const [gen, setGen] = useState({
    academy: academyInit === 'all' ? 'paralympic' : academyInit,
    sport_id: sportInit === 'all' ? '' : sportInit,
    athlete_name: '',
    age: '',
    disability_category: '',
    classification_code: '',
    current_level: 'beginner',
    weeks: 6,
    sessions_per_week: 3,
    goals_ar: '',
  });
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [a, b, c] = await Promise.all([
          api.get('/training/sports'),
          api.get('/training/programs'),
          api.get('/training/my-generated').catch(() => ({ data: [] })),
        ]);
        setSports(a.data);
        setPrograms(b.data);
        setGenerated(c.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load');
      }
      setLoading(false);
    })();
  }, []);

  const filtered = programs.filter((p) =>
    (!fAcademy || fAcademy === 'all' || p.academy === fAcademy) &&
    (!fSport || fSport === 'all' || p.sport_id === fSport) &&
    (!fLevel || fLevel === 'all' || p.level === fLevel)
  );

  const generate = async () => {
    if (!gen.sport_id || !gen.athlete_name || !gen.age || !gen.disability_category) {
      toast.error(isAr ? 'يرجى إكمال الحقول المطلوبة' : 'Please complete required fields');
      return;
    }
    setGenBusy(true);
    setGenResult(null);
    try {
      const payload = {
        ...gen,
        age: parseInt(gen.age, 10),
        weeks: parseInt(gen.weeks, 10),
        sessions_per_week: parseInt(gen.sessions_per_week, 10),
        language: lang,
      };
      const { data } = await api.post('/training/generate', payload);
      setGenResult(data);
      setGenerated((g) => [data, ...g]);
      toast.success(isAr ? 'تم إنشاء الخطّة' : 'Plan generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل توليد الخطة' : 'Generation failed'));
    }
    setGenBusy(false);
  };

  return (
    <main id="main" className="bg-gradient-to-b from-white to-emerald-50/30 min-h-screen py-8 px-4" data-testid="training-programs">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Badge className="mb-3 text-xs px-3 py-1 font-bold bg-emerald-500/15 text-emerald-700 border-emerald-300">
            <Target className="w-3 h-3 me-1" />{isAr ? 'مناهج التدريب الذكية' : 'Smart Training Curricula'}
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-black mb-2">{isAr ? 'بنك التدريبات ومولّد الخطط' : 'Training programs & AI planner'}</h1>
          <p className="text-sm text-muted-foreground">{isAr ? 'مكتبة برامج جاهزة + مولّد خطّة شخصية بالذكاء الاصطناعي.' : 'A library of programs + a personalised AI planner.'}</p>
        </div>

        <Tabs defaultValue="library">
          <TabsList className="mb-4">
            <TabsTrigger value="library" data-testid="tp-tab-library"><Library className="w-4 h-4 me-1" />{isAr ? 'المكتبة' : 'Library'}</TabsTrigger>
            <TabsTrigger value="generator" data-testid="tp-tab-generator"><Sparkles className="w-4 h-4 me-1" />{isAr ? 'مولّد AI' : 'AI Generator'}</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tp-tab-mine"><ListTree className="w-4 h-4 me-1" />{isAr ? 'خططي' : 'My plans'}</TabsTrigger>
          </TabsList>

          {/* LIBRARY TAB */}
          <TabsContent value="library">
            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              <div>
                <Label className="text-xs">{isAr ? 'الأكاديمية' : 'Academy'}</Label>
                <Select value={fAcademy} onValueChange={setFAcademy}>
                  <SelectTrigger data-testid="tp-filter-academy"><SelectValue placeholder={isAr ? 'الكل' : 'All'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{isAr ? 'الكل' : 'All'}</SelectItem>
                    <SelectItem value="paralympic">{isAr ? 'بارالمبية' : 'Paralympic'}</SelectItem>
                    <SelectItem value="special-olympics">{isAr ? 'الأولمبياد الخاص' : 'Special Olympics'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{isAr ? 'الرياضة' : 'Sport'}</Label>
                <Select value={fSport} onValueChange={setFSport}>
                  <SelectTrigger data-testid="tp-filter-sport"><SelectValue placeholder={isAr ? 'الكل' : 'All'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{isAr ? 'الكل' : 'All sports'}</SelectItem>
                    {sports.filter((s) => !fAcademy || s.academy === fAcademy || s.academy === 'both').map((s) => (
                      <SelectItem key={s.id} value={s.id}>{isAr ? s.name_ar : s.name_en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{isAr ? 'المستوى' : 'Level'}</Label>
                <Select value={fLevel} onValueChange={setFLevel}>
                  <SelectTrigger data-testid="tp-filter-level"><SelectValue placeholder={isAr ? 'الكل' : 'All'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{isAr ? 'الكل' : 'All'}</SelectItem>
                    <SelectItem value="beginner">{isAr ? 'مبتدئ' : 'Beginner'}</SelectItem>
                    <SelectItem value="intermediate">{isAr ? 'متوسط' : 'Intermediate'}</SelectItem>
                    <SelectItem value="advanced">{isAr ? 'متقدّم' : 'Advanced'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <div className="bg-stone-50 border border-border rounded-xl p-8 text-center" data-testid="tp-empty">
                <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="font-bold mb-1">{isAr ? 'لا توجد برامج بعد' : 'No programs yet'}</p>
                <p className="text-xs text-muted-foreground">{isAr ? 'استخدم مولّد الذكاء الاصطناعي أعلاه لإنشاء خطة.' : 'Use the AI generator above to create one.'}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tp-programs-grid">
                {filtered.map((p) => <ProgramCard key={p.id} p={p} isAr={isAr} />)}
              </div>
            )}
          </TabsContent>

          {/* GENERATOR TAB */}
          <TabsContent value="generator">
            <Card className="card-elevate">
              <CardContent className="p-6">
                <h2 className="text-xl font-black mb-4">{isAr ? 'مولّد خطّة شخصية بالذكاء الاصطناعي' : 'AI Personal Plan Generator'}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{isAr ? 'الأكاديمية' : 'Academy'}</Label>
                    <Select value={gen.academy} onValueChange={(v) => setGen({ ...gen, academy: v })}>
                      <SelectTrigger data-testid="gen-academy"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paralympic">{isAr ? 'بارالمبية' : 'Paralympic'}</SelectItem>
                        <SelectItem value="special-olympics">{isAr ? 'الأولمبياد الخاص' : 'Special Olympics'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'الرياضة *' : 'Sport *'}</Label>
                    <Select value={gen.sport_id} onValueChange={(v) => setGen({ ...gen, sport_id: v })}>
                      <SelectTrigger data-testid="gen-sport"><SelectValue placeholder={isAr ? 'اختر' : 'Select'} /></SelectTrigger>
                      <SelectContent>
                        {sports.filter((s) => s.academy === gen.academy || s.academy === 'both').map((s) => (
                          <SelectItem key={s.id} value={s.id}>{isAr ? s.name_ar : s.name_en}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'اسم الرياضي *' : 'Athlete name *'}</Label>
                    <Input value={gen.athlete_name} onChange={(e) => setGen({ ...gen, athlete_name: e.target.value })} data-testid="gen-name" />
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'العمر *' : 'Age *'}</Label>
                    <Input type="number" value={gen.age} onChange={(e) => setGen({ ...gen, age: e.target.value })} data-testid="gen-age" />
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'نوع الإعاقة *' : 'Disability *'}</Label>
                    <Select value={gen.disability_category} onValueChange={(v) => setGen({ ...gen, disability_category: v })}>
                      <SelectTrigger data-testid="gen-disability"><SelectValue placeholder={isAr ? 'اختر' : 'Select'} /></SelectTrigger>
                      <SelectContent>
                        {DISABILITY_OPTS.map((d) => <SelectItem key={d.id} value={d.id}>{isAr ? d.ar : d.en}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'كود التصنيف (اختياري)' : 'Classification code (opt.)'}</Label>
                    <Input placeholder="T44 / S6 / T20..." value={gen.classification_code} onChange={(e) => setGen({ ...gen, classification_code: e.target.value })} data-testid="gen-code" />
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'المستوى' : 'Level'}</Label>
                    <Select value={gen.current_level} onValueChange={(v) => setGen({ ...gen, current_level: v })}>
                      <SelectTrigger data-testid="gen-level"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">{isAr ? 'مبتدئ' : 'Beginner'}</SelectItem>
                        <SelectItem value="intermediate">{isAr ? 'متوسط' : 'Intermediate'}</SelectItem>
                        <SelectItem value="advanced">{isAr ? 'متقدّم' : 'Advanced'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">{isAr ? 'الأسابيع' : 'Weeks'}</Label>
                      <Input type="number" min={2} max={16} value={gen.weeks} onChange={(e) => setGen({ ...gen, weeks: e.target.value })} data-testid="gen-weeks" />
                    </div>
                    <div>
                      <Label className="text-xs">{isAr ? 'جلسات/أسبوع' : 'Sess/wk'}</Label>
                      <Input type="number" min={1} max={6} value={gen.sessions_per_week} onChange={(e) => setGen({ ...gen, sessions_per_week: e.target.value })} data-testid="gen-sess" />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">{isAr ? 'الأهداف' : 'Goals'}</Label>
                    <Textarea rows={2} value={gen.goals_ar} onChange={(e) => setGen({ ...gen, goals_ar: e.target.value })} data-testid="gen-goals" />
                  </div>
                </div>
                <Button onClick={generate} disabled={genBusy} className="mt-4 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="gen-submit">
                  {genBusy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />{isAr ? 'يولّد...' : 'Generating...'}</> : <><Sparkles className="w-4 h-4 me-2" />{isAr ? 'أنشئ الخطّة' : 'Generate plan'}</>}
                </Button>

                {/* Result preview */}
                {genResult && (
                  <div className="mt-6 border-t pt-5" data-testid="gen-result">
                    <h3 className="text-lg font-black mb-2">{genResult.plan?.[isAr ? 'title_ar' : 'title_en'] || genResult.plan?.title_ar}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{genResult.plan?.[isAr ? 'summary_ar' : 'summary_en'] || genResult.plan?.summary_ar}</p>
                    {Array.isArray(genResult.plan?.equipment_ar) && genResult.plan.equipment_ar.length > 0 && (
                      <div className="mb-3 text-xs"><strong>{isAr ? 'المعدات: ' : 'Equipment: '}</strong>{genResult.plan.equipment_ar.join('، ')}</div>
                    )}
                    {Array.isArray(genResult.plan?.weeks) && (
                      <div className="space-y-3">
                        {genResult.plan.weeks.slice(0, 4).map((w, i) => (
                          <Card key={i} className="card-elevate">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-4 h-4 text-emerald-600" />
                                <span className="font-bold text-sm">{isAr ? `الأسبوع ${w.week}` : `Week ${w.week}`}</span>
                                <Badge variant="outline" className="text-[10px]">{w[isAr ? 'focus_ar' : 'focus_en'] || w.focus_ar}</Badge>
                              </div>
                              <div className="grid sm:grid-cols-2 gap-2">
                                {(w.sessions || []).slice(0, 4).map((s, j) => (
                                  <div key={j} className="bg-stone-50 rounded p-2 text-xs">
                                    <div className="font-bold mb-1">{s[isAr ? 'title_ar' : 'title_en'] || s.title_ar} · {s.duration_min || 60}&apos;</div>
                                    <div className="text-muted-foreground leading-relaxed line-clamp-3">{s.main_ar || s.main_en}</div>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                    {Array.isArray(genResult.plan?.red_flags_ar) && genResult.plan.red_flags_ar.length > 0 && (
                      <div className="mt-4 bg-rose-50 border border-rose-200 rounded-lg p-3">
                        <div className="flex items-center gap-1 mb-1"><AlertTriangle className="w-4 h-4 text-rose-600" /><strong className="text-rose-700 text-sm">{isAr ? 'إشارات تحذيرية' : 'Red flags'}</strong></div>
                        <ul className="list-disc list-inside text-xs text-rose-800 space-y-0.5">
                          {genResult.plan.red_flags_ar.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MY PLANS TAB */}
          <TabsContent value="mine">
            {generated.length === 0 ? (
              <div className="bg-stone-50 border border-border rounded-xl p-8 text-center" data-testid="tp-mine-empty">
                <Activity className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                <p className="font-bold">{isAr ? 'لم تنشئ أيّ خطّة بعد' : 'No generated plans yet'}</p>
                <p className="text-xs text-muted-foreground">{isAr ? 'استخدم تبويب مولّد AI لإنشاء أوّل خطة.' : 'Use the AI Generator tab to create your first plan.'}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="tp-mine-grid">
                {generated.map((g) => (
                  <Card key={g.id} className="card-elevate" data-testid={`tp-mine-${g.id}`}>
                    <CardContent className="p-4">
                      <div className="text-xs text-muted-foreground mb-1">{g.athlete_name} · {g.athlete_age} · {g.disability_category}{g.classification_code ? ` · ${g.classification_code}` : ''}</div>
                      <h3 className="font-black text-sm mb-1">{g.plan?.[isAr ? 'title_ar' : 'title_en'] || g.plan?.title_ar}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2">{g.plan?.[isAr ? 'summary_ar' : 'summary_en'] || g.plan?.summary_ar}</p>
                      <div className="mt-2 text-[11px] text-muted-foreground">{g.weeks_count} {isAr ? 'أسبوع' : 'wk'} · {g.sessions_per_week}× {isAr ? 'أسبوعياً' : '/wk'} · {g.level}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
